import React, { useCallback } from 'react'
import {
   useTable,
   usePagination,
   useResizeColumns,
   useBlockLayout,
} from 'react-table'
import { Row } from 'react-grid-system'

import Pagination from '../../../components/common/pagination/pagination'
import { Alert, LoadingOverlay } from '../../common'
import { Severity } from '../../../types'
import { Actions, TABLE, Views } from '../../../constants/tables'

import Table from './Table'
import ItemCards from './itemCards/itemCards'
import Cards from './cards/cards'

import * as styles from './table.module.scss'
import { TableWrapperProps } from './types'
import AuditCards from './auditCards/auditCards'
import SmartAuditRuleCards from './smartAuditRuleCards/smartAuditRuleCards'



const TableWrapper = ({
   data = [],
   columns = [],
   totalCount = 0,
   getItems,
   isPageReset,
   setIsPageReset,
   setPageSized,
   extraClassName,
   isLoading,
   searchCriteria,
   viewValue = Views.Table,
   initialState = {},
   toolStatus,
   hidePagination,
   setBypass,
   getItem,
   isListLoading,
   isListRefetching,
   hideResultsTitle = false,
   isScrollView = false,
   isCart = false,
   tableType,
   showActionForm,
   onViewItemsClick,
   isEmployeeUser,
   isAuditWorkingView,
   currentUserEmail
}: TableWrapperProps) => {
   const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      prepareRow,
      page,
      setPageSize,
      state: { pageSize },
   } = useTable(
      {
         columns,
         data,
         initialState,
      },
      usePagination,
      useResizeColumns,
      useBlockLayout
   )

   const onDeleteClick = useCallback(
      (id: string) => showActionForm(Actions.Delete, id),
      [showActionForm]
   )

   const onExportClick = useCallback(
      (id: string) => showActionForm(Actions.Export, id),
      [showActionForm]
   )

   const onReviewedClick = useCallback(
      (id: string) => showActionForm(Actions.Reviewed, id),
      [showActionForm]
   )

   return (
      <>
         {!isLoading && Boolean(!totalCount) && (
            <div
               className={
                  isScrollView ? styles.scrollViewWrapper : styles.alertWrapper
               }
            >
               <Alert
                  message={isCart ? TABLE.NO_RESULTS_CART : TABLE.NO_RESULTS}
                  severity={Severity.INFO}
               />
            </div>
         )}
         {Boolean(totalCount) && data?.length > 0 && (
            <div className={'table-container ' + extraClassName}>
               {/* Cards views */}
               {viewValue === Views.Cards && (
                  <>
                     <div className={styles.tableHeaderArea}>
                        {(isListLoading || isListRefetching) && (
                           <LoadingOverlay />
                        )}
                        <div className={styles.results}>
                           {totalCount} {TABLE.COUNT_LABEL}
                        </div>
                     </div>

                     <Row gutterWidth={tableType === 'Audits' ? 16 : 30}>
                        {tableType === 'Items' && (
                           <ItemCards
                              data={data}
                              setBypass={setBypass}
                              getItem={getItem}
                           />
                        )}

                        {(tableType === 'Connections' ||
                           tableType === 'Locations') && (
                           <Cards
                              data={data}
                              onCardClick={getItem}
                              onDeleteClick={onDeleteClick}
                              onViewItemsClick={onViewItemsClick}
                              isEmployeeUser={isEmployeeUser}
                              tableType={tableType}
                              currentUserEmail={currentUserEmail}
                           />
                        )}

                        {tableType === 'Audits' && (
                           <AuditCards
                              data={data}
                              onCardClick={getItem}
                              onDeleteClick={onDeleteClick}
                              onExportClick={onExportClick}
                              onReviewedClick={onReviewedClick}
                              isEmployeeUser={isEmployeeUser}
                              isWorkingView={isAuditWorkingView}
                           />
                        )}

                        {tableType === 'Smart Audit Rules' && (
                           <SmartAuditRuleCards
                              data={data}
                              onCardClick={getItem}
                              onDeleteClick={onDeleteClick}
                           />
                        )}
                     </Row>
                  </>
               )}

               {/* Table view */}
               {viewValue === Views.Table && (
                  <>
                     <div
                        className={
                           isScrollView ? styles.scrollViewWrapper : null
                        }
                     >
                        <Table
                           totalCount={totalCount}
                           getTableProps={getTableProps}
                           headerGroups={headerGroups}
                           getTableBodyProps={getTableBodyProps}
                           page={page}
                           prepareRow={prepareRow}
                           rowAction={getItem}
                           hideResultsTitle={hideResultsTitle}
                           isCart={isCart}
                           tableType={tableType}
                        />
                     </div>
                     {isScrollView && <br />}
                  </>
               )}

               {!hidePagination && (
                  <Pagination
                     setPageSize={setPageSize}
                     setPageSized={setPageSized}
                     pageSize={pageSize}
                     totalCount={totalCount}
                     getItems={getItems}
                     isPageReset={isPageReset}
                     setIsPageReset={setIsPageReset}
                     searchCriteria={searchCriteria}
                     toolStatus={toolStatus}
                     pageDataLength={page?.length}
                  />
               )}
            </div>
         )}
      </>
   )
}

export default TableWrapper
